// frontend/src/pages/client/TransactionsClient.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../../services/api';
import {
  ClockIcon,
  ListBulletIcon,
  ShoppingCartIcon,
  MinusCircleIcon,
  ChartBarSquareIcon,
  CubeIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/solid';
import './TransactionsClient.css';

const TransactionsClient = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [filter, setFilter]             = useState('all');
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(1);
  const limit = 10;

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const typeParam = filter !== 'all' ? `&type=${filter}` : '';
      const res = await API.get(`/client/transactions?limit=${limit}&offset=${(page - 1) * limit}${typeParam}`);
      setTransactions(res.data.transactions || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Erreur transactions:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, page, limit]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const totalPages = Math.ceil(total / limit);

  const filterIconStyle = { width: 18, height: 18, flexShrink: 0 };
  const filters = [
    { value: 'all',      label: 'Toutes',        icon: <ListBulletIcon style={filterIconStyle} />     },
    { value: 'purchase', label: 'Achats',         icon: <ShoppingCartIcon style={filterIconStyle} />   },
    { value: 'consume',  label: 'Consommations',  icon: <MinusCircleIcon style={filterIconStyle} />    },
  ];

  return (
    <motion.div className="tc-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>

      {/* En-tête */}
      <motion.div
        className="tc-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1><ChartBarSquareIcon style={{ width: 18, height: 18, flexShrink: 0 }} /> Mes transactions</h1>
          <p>Historique complet de vos achats et consommations de points</p>
        </div>
        <div className="tc-total-badge">
          <i className="fas fa-receipt"></i>
          <span>{total} transaction{total > 1 ? 's' : ''}</span>
        </div>
      </motion.div>

      {/* Filtres */}
      <motion.div
        className="tc-filters"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        {filters.map(f => (
          <button
            key={f.value}
            className={`tc-filter-btn ${filter === f.value ? 'active' : ''}`}
            onClick={() => setFilter(f.value)}
          >
            {f.icon} {f.label}
          </button>
        ))}
      </motion.div>

      {/* Table */}
      <motion.div
        className="tc-card"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
      >
        {loading ? (
          <div className="tc-loading">
            <div className="tc-spinner"></div>
            <p>Chargement...</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="tc-empty">
            <div className="tc-empty-icon"><i className="fas fa-history"></i></div>
            <h3>Aucune transaction</h3>
            <p>
              {filter !== 'all'
                ? 'Aucune transaction de ce type'
                : 'Vos transactions apparaîtront ici après votre premier achat'}
            </p>
          </div>
        ) : (
          <>
            <div className="tc-table-wrap">
              <table className="tc-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Service</th>
                    <th>Type</th>
                    <th>Points</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {transactions.map((tx, i) => (
                      <motion.tr
                        key={tx.id}
                        initial={{ opacity: 0, x: -12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.03 }}
                      >
                        <td className="tc-id">#{tx.id}</td>
                        <td className="tc-date">
                          {new Date(tx.created_at).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'short', year: 'numeric'
                          })}
                          <span className="tc-time">
                            {new Date(tx.created_at).toLocaleTimeString('fr-FR', {
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </td>
                        <td>
                          <div className="tc-service">
                            <div className="tc-service-icon">
                              <i className="fas fa-cube"></i>
                            </div>
                            {tx.service_name || '—'}
                          </div>
                        </td>
                        <td>
                          <span className={`tc-badge tc-badge--${tx.transaction_type === 'purchase' ? 'purchase' : 'consume'}`}>
                            <i className={`fas ${tx.transaction_type === 'purchase' ? 'fa-cart-plus' : 'fa-minus'}`}></i>
                            {tx.transaction_type === 'purchase' ? 'Achat' : 'Consommation'}
                          </span>
                        </td>
                        <td className={tx.transaction_type === 'purchase' ? 'tc-positive' : 'tc-negative'}>
                          {tx.transaction_type === 'purchase' ? '+' : '-'}{tx.points_amount} pts
                        </td>
                        <td>
                          <span className="tc-badge tc-badge--success">
                            <i className="fas fa-check"></i> Réussi
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="tc-pagination">
                <button
                  className="tc-page-btn"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button
                      key={p}
                      className={`tc-page-btn ${page === p ? 'active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  );
                })}
                <button
                  className="tc-page-btn"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

export default TransactionsClient;